// src/components/PlanInfoCard.tsx

import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { CreditCard, Calendar, Check, Crown } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { STANDARD_PLAN_BENEFITS } from '@/lib/planFeatures';
import { UpgradeModal } from '@/components/UpgradeModal';

export function PlanInfoCard() {
  const { user } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const plan = user?.plan || 'Free';
  const planStartDate = user?.planStartDate;
  const planEndDate = user?.planEndDate;

  const isStandard = plan === 'Standard';

  return (
    <>
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-green-600" />
          プラン情報
        </h2>

        <div className="space-y-6">
          {/* 現在のプラン */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${
            isStandard 
              ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' 
              : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
          }`}>
            <div>
              <p className="text-sm text-gray-600 mb-1">現在のプラン</p>
              <p className="text-2xl font-bold text-gray-900">{plan}</p>
            </div>
            <div
              className={`px-4 py-2 rounded-full font-semibold flex items-center gap-2 ${
                isStandard
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {isStandard && <Crown className="w-4 h-4" />}
              {isStandard ? 'Standard' : '無料'}
            </div>
          </div>

          {/* プラン期間 */}
          {isStandard && planStartDate && planEndDate && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">契約期間</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-gray-600 mb-1">開始日</p>
                  <p className="font-semibold text-gray-900">
                    {format(planStartDate.toDate(), 'yyyy年M月d日', { locale: ja })}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-gray-600 mb-1">終了日</p>
                  <p className="font-semibold text-gray-900">
                    {format(planEndDate.toDate(), 'yyyy年M月d日', { locale: ja })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* プラン特典 */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              {isStandard ? 'Standardプランの特典' : 'Standardプランにアップグレードすると'}
            </p>
            <ul className="space-y-2">
              {STANDARD_PLAN_BENEFITS.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className={`w-4 h-4 ${isStandard ? 'text-green-600' : 'text-gray-400'}`} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* アップグレードボタン */}
          {!isStandard && (
            <button 
              onClick={() => setShowUpgradeModal(true)}
              className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg shadow-orange-500/30"
            >
              Standardプランにアップグレード
            </button>
          )}
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="すべての機能"
      />
    </>
  );
}
